/**
 * Normalización del español para el asistente: texto sin acentos ni mayúsculas, números
 * escritos, duraciones, conjuntos de días (con excepciones), horas, franjas y fechas
 * relativas. Funciones puras, sin red.
 *
 * `Phrase` mantiene el texto original y su copia normalizada con la MISMA longitud: al
 * extraer un dato, el tramo se sustituye por espacios en ambas, así los índices siguen
 * alineados y lo que queda (por ejemplo, el nombre de la actividad) conserva sus acentos.
 */
import { addDays, isoWeekday, isValidLocalDate, type LocalDate } from '@habitos/engine';

/** Minúsculas y sin acentos, carácter a carácter (misma longitud que el original). */
export function fold(s: string): string {
  return s
    .split('')
    .map((c) => c.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()[0] ?? c)
    .join('');
}

/** Normalizada + signos de puntuación como espacios (salvo en números: 1,5 · 18:30 · 3/11). */
function normalizeAligned(raw: string): string {
  return fold(raw).replace(/[¿?¡!;()"«»“”]|(?<!\d)[.,]|[.,](?!\d)/g, ' ');
}

export class Phrase {
  norm: string;
  constructor(public raw: string) {
    this.norm = normalizeAligned(raw);
  }
  /** Busca en la normalizada, borra el tramo en ambas copias y devuelve la coincidencia. */
  take(re: RegExp): RegExpExecArray | null {
    const m = re.exec(this.norm);
    if (!m) return null;
    const blank = ' '.repeat(m[0].length);
    const a = m.index;
    const b = a + m[0].length;
    this.raw = this.raw.slice(0, a) + blank + this.raw.slice(b);
    this.norm = this.norm.slice(0, a) + blank + this.norm.slice(b);
    return m;
  }
  has(re: RegExp): boolean {
    return re.test(this.norm);
  }
  /** Texto restante, sin espacios dobles ni conectores sueltos en los extremos. */
  rest(): string {
    return this.raw
      .replace(/\s+/g, ' ')
      .replace(/^[\s,.:;-]+|[\s,.:;!?¿¡-]+$/g, '')
      .trim();
  }
}

/** Normaliza para comparar (búsquedas y coincidencias de nombres). */
export function simplify(s: string): string {
  return normalizeAligned(s).replace(/\s+/g, ' ').trim();
}

// --- Números -------------------------------------------------------------------

const NUMBER_WORDS: Record<string, number> = {
  cero: 0, un: 1, uno: 1, una: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5, seis: 6, siete: 7, ocho: 8, nueve: 9, diez: 10,
  once: 11, doce: 12, trece: 13, catorce: 14, quince: 15, dieciseis: 16, diecisiete: 17, dieciocho: 18, diecinueve: 19,
  veinte: 20, veinticinco: 25, treinta: 30, cuarenta: 40, cincuenta: 50, sesenta: 60, noventa: 90, cien: 100, ciento: 100,
};
const TENS = '(?:veinte|treinta|cuarenta|cincuenta)';
const UNITS_1_9 = '(?:uno|una|dos|tres|cuatro|cinco|seis|siete|ocho|nueve)';
/** Número en cifras o palabras ("treinta y cinco" incluido). */
export const NUM = `(\\d+(?:[.,]\\d+)?|${TENS} y ${UNITS_1_9}|${Object.keys(NUMBER_WORDS).sort((a, b) => b.length - a.length).join('|')})`;

export function numberValue(s: string): number | null {
  const t = s.trim();
  if (/^\d+(?:[.,]\d+)?$/.test(t)) return Number(t.replace(',', '.'));
  const compound = /^(\w+) y (\w+)$/.exec(t);
  if (compound) {
    const a = NUMBER_WORDS[compound[1]!];
    const b = NUMBER_WORDS[compound[2]!];
    return a !== undefined && b !== undefined ? a + b : null;
  }
  return NUMBER_WORDS[t] ?? null;
}

// --- Duraciones -----------------------------------------------------------------

/** Duración en minutos: "30 minutos", "media hora", "una hora y media", "1h30", "45'". */
export function takeDuration(p: Phrase): number | null {
  const pre = '(?:durante |por |unos |unas |de )?';
  let m = p.take(new RegExp(`\\b${pre}(\\d{1,2})\\s*h\\s*(\\d{2})\\b`));
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  m = p.take(new RegExp(`\\b${pre}${NUM}\\s*(?:horas?|hs?)\\s*y\\s*(media|cuarto|${NUM}\\s*(?:minutos?|min))\\b`));
  if (m) {
    const h = numberValue(m[1]!);
    const extra = m[2] === 'media' ? 30 : m[2] === 'cuarto' ? 15 : numberValue(m[3] ?? '');
    if (h !== null && extra !== null) return Math.round(h * 60 + extra);
  }
  if (p.take(new RegExp(`\\b${pre}tres cuartos de hora\\b`))) return 45;
  if (p.take(new RegExp(`\\b${pre}(?:un )?cuarto de hora\\b`))) return 15;
  if (p.take(new RegExp(`\\b${pre}(?:una )?media hora\\b`))) return 30;
  m = p.take(new RegExp(`\\b${pre}${NUM}\\s*(?:minutos?|mins?|min|')(?=\\s|$)`));
  if (m) {
    const v = numberValue(m[1]!);
    if (v !== null) return Math.round(v);
  }
  m = p.take(new RegExp(`\\b${pre}${NUM}\\s*(?:horas?|hs?)\\b`));
  if (m) {
    const v = numberValue(m[1]!);
    if (v !== null) return Math.round(v * 60);
  }
  return null;
}

// --- Días de la semana ------------------------------------------------------------

export const WEEKDAY = '(lunes|martes|miercoles|jueves|viernes|sabados?|domingos?)';
const WEEKDAY_NUM: Record<string, number> = { lunes: 1, martes: 2, miercoles: 3, jueves: 4, viernes: 5, sabado: 6, sabados: 6, domingo: 7, domingos: 7 };
export const weekdayNumber = (w: string): number => WEEKDAY_NUM[w]!;
const DAY_ITEM = `(?:(?:los|el|la|cada) )?(?:${WEEKDAY}|fines? de semana|finde)`;
const DAY_LIST = `${DAY_ITEM}(?:\\s*(?:,|y|e|ni|o)?\\s+${DAY_ITEM})*`;

function daysIn(text: string): number[] {
  const out = new Set<number>();
  if (/\bfines? de semana\b|\bfinde\b/.test(text)) [6, 7].forEach((d) => out.add(d));
  for (const m of text.matchAll(new RegExp(`\\b${WEEKDAY}\\b`, 'g'))) out.add(weekdayNumber(m[1]!));
  return [...out].sort((a, b) => a - b);
}

export const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];

/**
 * Cantidad semanal: "seis horas por semana", "90 minutos a la semana", "3 h semanales".
 * Se toma antes que la frecuencia para que "por semana" no se lea como días.
 */
export function takeWeeklyAmount(p: Phrase): number | null {
  const m = p.take(new RegExp(`\\b((?:${NUM}\\s*(?:horas?|hs?|minutos?|mins?)(?:\\s*y\\s*media)?|media hora|una hora y media))\\s+(?:(?:por|a la|cada|en la|en|a|por la)\\s+semana|semanales?|a la semana)\\b`));
  if (!m) return null;
  const minutes = takeDuration(new Phrase(m[1]!));
  return minutes !== null && minutes >= 15 && minutes <= 3000 ? minutes : null;
}

/**
 * Días libres: "déjame libre el domingo", "quiero los sábados libres", "nada el domingo",
 * "descansar el domingo". Devuelve los días o null.
 */
export function takeFreeDays(p: Phrase): number[] | null {
  const m =
    p.take(new RegExp(`\\b(?:dejame|deja|dejar|quiero|necesito|tener|mantener|mantene|que quede|quede)?\\s*(?:libres?|sin nada|descansar|descanso)\\s+(?:el |los |la )?(${DAY_LIST})`)) ??
    p.take(new RegExp(`\\b(?:dejame |quiero |necesito |tener )?(${DAY_LIST})\\s+(?:libres?|de descanso|para descansar|sin nada)\\b`)) ??
    p.take(new RegExp(`\\bnada (?:el |los )?(${DAY_LIST})\\b`));
  if (!m) return null;
  const days = daysIn(m[1]!);
  return days.length && days.length < 7 ? days : null;
}

/** Reparte `n` días a lo largo de los permitidos, separados; `variant` desplaza el patrón. */
export function spreadDays(n: number, allowed: number[] = ALL_DAYS, variant = 0): number[] {
  const pool = [...new Set(allowed)].sort((a, b) => a - b);
  if (n >= pool.length) return pool;
  const out = new Set<number>();
  const step = pool.length / n;
  for (let i = 0; i < n; i++) out.add(pool[Math.floor(i * step + variant) % pool.length]!);
  for (const d of pool) if (out.size < n) out.add(d);
  return [...out].sort((a, b) => a - b);
}

export type DaySet =
  | { type: 'days'; days: number[]; except: number[] }
  | { type: 'quota'; times: number };

/**
 * Frecuencia: "todos los días", "al día", "entre semana", "fines de semana", "de lunes a
 * viernes", "lunes, miércoles y viernes", "3 veces por semana", y excepciones ("excepto los
 * miércoles", "todos los días menos el domingo").
 */
export function takeDaySet(p: Phrase): DaySet | null {
  const exc = p.take(new RegExp(`\\b(?:excepto|menos|salvo|quitando|sin contar|pero no)\\s+(${DAY_LIST})`));
  const except = exc ? daysIn(exc[1]!) : [];
  const minus = (days: number[]): DaySet => ({ type: 'days', days: days.filter((d) => !except.includes(d)), except });

  const quota = p.take(new RegExp(`\\b${NUM}\\s*(?:veces|vez|dias)\\s*(?:a la|por|por la|cada|a|en la|en)\\s*semana\\b`));
  if (quota) {
    const n = numberValue(quota[1]!);
    if (n !== null && n >= 1 && n < 7) return { type: 'quota', times: Math.round(n) };
    if (n !== null && n >= 7) return { type: 'days', days: ALL_DAYS, except: [] };
  }
  if (p.take(/\b(?:de lunes a viernes|entre semana|(?:los )?dias laborables|laborables|dias de semana)\b/)) return minus([1, 2, 3, 4, 5]);
  if (p.take(/\b(?:todos los |los )?fines? de semana\b|\bel finde\b/)) return minus([6, 7]);
  const range = p.take(new RegExp(`\\bde ${WEEKDAY} a ${WEEKDAY}\\b`));
  if (range) {
    const a = weekdayNumber(range[1]!);
    const b = weekdayNumber(range[2]!);
    const days: number[] = [];
    for (let d = a; days.length < 7; d = (d % 7) + 1) {
      days.push(d);
      if (d === b) break;
    }
    return minus(days.sort((x, y) => x - y));
  }
  const daily = p.take(/\b(?:todos los dias|todas las semanas|cada dia|a diario|al dia|por dia|diariamente|diario|toda la semana)\b/);
  if (daily) return minus(ALL_DAYS);
  const list = p.take(new RegExp(`\\b(?:los |cada |el )?${DAY_LIST}\\b`));
  if (list) {
    const days = daysIn(list[0]);
    if (days.length) return minus(days);
  }
  if (except.length) return minus(ALL_DAYS);
  return null;
}

// --- Horas y franjas -----------------------------------------------------------------

export type Slot = 'morning' | 'afternoon' | 'evening';
export const SLOT_RANGES: Record<Slot, { start: string; end: string }> = {
  morning: { start: '08:00', end: '12:00' },
  afternoon: { start: '15:00', end: '19:00' },
  evening: { start: '19:00', end: '22:00' },
};
/** Hora sugerida para una franja sin hora concreta. */
export const SLOT_DEFAULT: Record<Slot, string> = { morning: '08:00', afternoon: '18:00', evening: '21:00' };

export type TimeResult =
  | { type: 'time'; time: string }
  /** "a las 3": ¿03:00 o 15:00? Se pregunta. */
  | { type: 'ambiguous'; options: [string, string] }
  | { type: 'slot'; slot: Slot }
  | { type: 'invalid' }
  | { type: 'none' };

export const hhmm = (h: number, m: number) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
const HOUR = '(\\d{1,2}|una|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez|once|doce)';
const PART = '(?:\\s*(?:de la (manana|tarde|noche)|(am|pm)|de la madrugada))?';

function hourValue(s: string): number {
  return /^\d+$/.test(s) ? Number(s) : NUMBER_WORDS[s]!;
}

function resolveHour(h: number, min: number, part: string | undefined, explicitMinutes: boolean): TimeResult {
  if (part === 'tarde' || part === 'noche' || part === 'pm') {
    if (h < 12) h += 12;
  } else if ((part === 'manana' || part === 'am') && h === 12) {
    h = 0;
  } else if (!part && h >= 1 && h <= 6 && !explicitMinutes) {
    return { type: 'ambiguous', options: [hhmm(h, min), hhmm(h + 12, min)] };
  }
  if (h > 23 || min > 59) return { type: 'invalid' };
  return { type: 'time', time: hhmm(h, min) };
}

/** Hora concreta ("a las 7 de la tarde", "19:30", "8 y media", "7pm"), o franja ("por la tarde"). */
export function takeTime(p: Phrase): TimeResult {
  if (p.take(/\b(?:al |a )?mediodia\b/)) return { type: 'time', time: '12:00' };
  if (p.take(/\b(?:a )?medianoche\b/)) return { type: 'time', time: '00:00' };
  let m = p.take(new RegExp(`\\b(?:a las?|sobre las?|para las?|hacia las?|a eso de las?|tipo|desde las?)\\s+${HOUR}(?:[:.h](\\d{2}))?(?:\\s+y (media|cuarto)|\\s+menos cuarto)?\\s*(?:hs?|horas|en punto)?${PART}`));
  if (!m) m = p.take(new RegExp(`\\b(\\d{1,2})[:h](\\d{2})()\\s*(?:hs?)?${PART}`));
  // "19h" suelto no se toma como hora: se confundiría con una duración ("estudiar 2h").
  if (!m) m = p.take(/\b(\d{1,2})\s*(am|pm)\b()()()/);
  if (m) {
    // Grupos: 1 hora, 2 minutos, 3 "y media/cuarto", 4 parte del día, 5 am/pm.
    if (m[2] === 'am' || m[2] === 'pm') return resolveHour(hourValue(m[1]!), 0, m[2], false);
    let h = hourValue(m[1]!);
    let min = m[2] ? Number(m[2]) : 0;
    if (m[3] === 'media') min = 30;
    else if (m[3] === 'cuarto') min = 15;
    else if (/menos cuarto/.test(m[0])) {
      h = h - 1;
      min = 45;
    }
    const part = m[4] ?? m[5] ?? (/madrugada/.test(m[0]) ? 'am' : undefined);
    return resolveHour(h, min, part, Boolean(m[2]) || m[0].includes(':'));
  }
  if (p.take(/\b(?:por|en|a|de|durante) la manana\b|\btemprano\b|\bmananas\b/)) return { type: 'slot', slot: 'morning' };
  if (p.take(/\b(?:por|en|a|de|durante) la tarde\b|\btardes\b/)) return { type: 'slot', slot: 'afternoon' };
  if (p.take(/\b(?:por|en|a|de|durante) la noche\b|\bnoches\b|\bantes de dormir\b/)) return { type: 'slot', slot: 'evening' };
  return { type: 'none' };
}

export interface TimeRange {
  start: string;
  end: string;
}

/** Intervalo: "de 18 a 20", "entre las 7 y las 9 de la mañana", "de 18:00 a 20:30", o franja. */
export function takeRange(p: Phrase): TimeRange | { ambiguous: true } | null {
  const m = p.take(
    new RegExp(`\\b(?:de|desde|entre)\\s+(?:las?\\s+)?${HOUR}(?::(\\d{2}))?\\s*(?:hs?)?\\s+(?:a|hasta|y)\\s+(?:las?\\s+)?${HOUR}(?::(\\d{2}))?\\s*(?:hs?|horas)?${PART}`),
  );
  if (m) {
    let a = hourValue(m[1]!);
    let b = hourValue(m[3]!);
    const am = Number(m[2] ?? 0);
    const bm = Number(m[4] ?? 0);
    const part = m[5] ?? m[6];
    if (part === 'tarde' || part === 'noche' || part === 'pm') {
      if (a < 12) a += 12;
      if (b < 12) b += 12;
    } else if (!part && a >= 1 && a <= 6) {
      return { ambiguous: true };
    }
    if (b < a && b + 12 <= 23) b += 12;
    if (a > 23 || b > 23 || am > 59 || bm > 59 || hhmm(b, bm) <= hhmm(a, am)) return null;
    return { start: hhmm(a, am), end: hhmm(b, bm) };
  }
  const t = takeTime(p);
  if (t.type === 'slot') return SLOT_RANGES[t.slot];
  return null;
}

// --- Fechas ----------------------------------------------------------------------------

const MONTHS: Record<string, number> = {
  enero: 1, febrero: 2, marzo: 3, abril: 4, mayo: 5, junio: 6, julio: 7, agosto: 8, septiembre: 9, setiembre: 9, octubre: 10, noviembre: 11, diciembre: 12,
};

export type DateResult = { type: 'date'; date: LocalDate } | { type: 'invalid' } | { type: 'none' };

/**
 * Fecha: hoy, mañana (no "por la mañana"), pasado mañana, ayer, un día de la semana (la
 * próxima vez que cae), "dentro de 3 días", "en dos semanas", "el 25 de octubre", 25/10.
 */
export function takeDate(p: Phrase, today: LocalDate, allowPast = false): DateResult {
  if (p.take(/\bpasado manana\b/)) return { type: 'date', date: addDays(today, 2) };
  if (p.take(/(?<!\b(?:la|por|de|en) )\bmanana\b/)) return { type: 'date', date: addDays(today, 1) };
  if (p.take(/\bhoy\b|\besta (?:tarde|noche|manana)\b/)) return { type: 'date', date: today };
  if (allowPast && p.take(/\banteayer\b/)) return { type: 'date', date: addDays(today, -2) };
  if (allowPast && p.take(/\bayer\b/)) return { type: 'date', date: addDays(today, -1) };
  let m = p.take(new RegExp(`\\b(?:dentro de|en)\\s+${NUM}\\s+(dias?|semanas?)\\b`));
  if (m) {
    const n = numberValue(m[1]!);
    if (n !== null) return { type: 'date', date: addDays(today, Math.round(n) * (m[2]!.startsWith('semana') ? 7 : 1)) };
  }
  m = p.take(new RegExp(`\\b(?:el |este |el proximo |proximo |el siguiente )?${WEEKDAY}(?: que viene| proximo)?\\b`));
  if (m) {
    const diff = (weekdayNumber(m[1]!) - isoWeekday(today) + 7) % 7 || 7;
    return { type: 'date', date: addDays(today, diff) };
  }
  const y = Number(today.slice(0, 4));
  const resolve = (yy: number | null, mm: number, dd: number): DateResult => {
    const make = (year: number) => `${year}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
    let d = make(yy ?? y);
    if (!isValidLocalDate(d)) return { type: 'invalid' };
    if (yy === null && d < today && !allowPast) d = make(y + 1);
    return isValidLocalDate(d) ? { type: 'date', date: d as LocalDate } : { type: 'invalid' };
  };
  m = p.take(new RegExp(`\\b(?:el )?(\\d{1,2}) de (${Object.keys(MONTHS).join('|')})(?: de (\\d{4}))?\\b`));
  if (m) return resolve(m[3] ? Number(m[3]) : null, MONTHS[m[2]!]!, Number(m[1]));
  m = p.take(/\b(?:el )?(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b/);
  if (m) return resolve(m[3] ? Number(m[3].length === 2 ? `20${m[3]}` : m[3]) : null, Number(m[2]), Number(m[1]));
  return { type: 'none' };
}

/** Semana a la que se refiere el texto: la actual (desde hoy) o la siguiente. */
export function takeWeek(p: Phrase, today: LocalDate, weekStart: number): { from: LocalDate; to: LocalDate; which: 'this' | 'next' } | null {
  const startOf = (d: LocalDate) => addDays(d, -((isoWeekday(d) - weekStart + 7) % 7));
  if (p.take(/\b(?:la )?(?:semana que viene|proxima semana|semana proxima|siguiente semana)\b/)) {
    const from = addDays(startOf(today), 7);
    return { from, to: addDays(from, 6), which: 'next' };
  }
  if (p.take(/\b(?:esta|la|mi) semana\b/)) return { from: today, to: addDays(startOf(today), 6), which: 'this' };
  return null;
}

/** Nombres de los días ("lunes, martes y jueves") en el idioma de la interfaz. */
export function dayNames(days: number[], locale: 'es' | 'en'): string {
  const fmt = new Intl.DateTimeFormat(locale === 'es' ? 'es-ES' : 'en-US', { weekday: 'long', timeZone: 'UTC' });
  // 2024-01-01 fue lunes.
  const names = days.map((d) => fmt.format(new Date(Date.UTC(2024, 0, d))));
  return new Intl.ListFormat(locale === 'es' ? 'es-ES' : 'en-US', { style: 'long', type: 'conjunction' }).format(names);
}

export function addMinutes(time: string, minutes: number): string {
  const total = Math.min(23 * 60 + 59, Number(time.slice(0, 2)) * 60 + Number(time.slice(3)) + minutes);
  return hhmm(Math.floor(total / 60), total % 60);
}

export const toMinutes = (time: string) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3));

/** Fecha legible ("lunes, 19 de octubre"), igual que fmtDate de la interfaz. */
export function formatDay(d: string, locale: 'es' | 'en', opts: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long' }): string {
  const s = new Intl.DateTimeFormat(locale === 'es' ? 'es-ES' : 'en-US', { ...opts, timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));
  return s.charAt(0).toLocaleUpperCase() + s.slice(1);
}
